import {CardContent, CircularProgress} from '@mui/material';
import React, {FunctionComponent, useCallback, useEffect, useRef, useState} from 'react';
import {SubmitHandler, useForm} from 'react-hook-form';
import {useTranslation} from 'react-i18next';
import Swal from 'sweetalert2';
import {
  errorBigIcon, expIcon,
  iconNotifySuccess,
  iconNotifyWarningInfo,
  toastSuccess
} from '../../../../assets/icons/icons';
import NoSubscriptionBlock from '../../../../components/Blocks/NoSubscriptionBlock/NoSubscriptionBlock';
import ValidationErrorBlock from '../../../../components/Blocks/ValidationErrorBlock/ValidationErrorBlock';
import {Button, Card, FormControl, TextField} from '../../../../components/StyledComponents';
import BackButton from '../../../../components/Buttons/BackButton';
import EditSelectionButton from '../../../../components/Buttons/EditSelectionButton';
import {EPlatformsNames, ESubscriptionPlans, ETrialStatus} from '../../../../models/consts';
import {IError, IShopifyUserAddEditProps} from '../../../../models/inner-models';
import {IGithubUser} from '../../../../models/models';
import {
  confirmShopify, deleteShop,
  fetchShopifyUser,
  getShopifyLoginUrl,
  getUser,
  sendConfirmShopify
} from '../../../../store/actions';
import {useAppDispatch, useAppSelector} from '../../../../store/hooks';
import {shopifyTrialStatus} from '../../../../store/selectors';
import {decoratePopUpMessage} from '../../../../utils/popUpTextDecorator';
import './style.scss';
import Platform from "../../../../models/platforms-data";
import {useNavigate, useSearchParams} from "react-router";

const ShopifyUserAddEdit: FunctionComponent<IShopifyUserAddEditProps> = ({
                                                                         user,
                                                                         subPlan,
                                                                         onClose,
                                                                         page
                                                                       }: IShopifyUserAddEditProps) => {
    const dispatch = useAppDispatch();
    const {t: translate} = useTranslation();
    const navigate = useNavigate();
    const trialState = useAppSelector(shopifyTrialStatus);
    const [isUpdatingUser, setIsUpdatingUser] = useState<boolean>(false);
    const installBtn = useRef<HTMLButtonElement>(null);
    const [codeSent, setCodeSent] = useState<boolean>(false);
    const [searchParams] = useSearchParams();
    const [isDeleteInProgress, setIsDeleteInProgress] = useState<boolean>(false);

    const {register, formState: {errors, isValid, isDirty}, getValues, handleSubmit, reset} = useForm({
      mode: 'onChange',
    });

    const sendConfirmationCode = useCallback(async () => {
      try {
        const message = await dispatch(sendConfirmShopify()).unwrap();
        setCodeSent(true);

        await Swal.fire({
          imageUrl: iconNotifySuccess,
          title: translate('notifications.titles.success'),
          text: decoratePopUpMessage(message),
          cancelButtonText: translate('notifications.choices.close'),
          showCancelButton: true,
          confirmButtonText: translate('notifications.choices.enter_code'),
          preConfirm: () => {
            return confirmUser();
          },
        });
      } catch (e) {
        const error = e as IError;
        setCodeSent(false);

        await Swal.fire({
          title: translate('notifications.titles.error'),
          text: decoratePopUpMessage(error.error as string),
          showConfirmButton: true,
          confirmButtonText: error.code === "err.app.missing" ?
            translate('forms.shopify_add_edit_user.github_app') :
            error.code === "err.app.delay" ?
              translate('notifications.choices.enter_code') :
              translate('common.default_pages.contact_us'),
          showCancelButton: true,
          cancelButtonText: translate('notifications.choices.close'),
          imageUrl: errorBigIcon,
        })
          .then(res => {
            if (res.value) {
              switch (error?.code) {
                case "err.app.missing":
                  window.open(user?.redirectUrl, '_blank')
                  break;
                case "err.app.delay":
                  return confirmUser();
                default:
                  window.location.pathname = '/contact'
                  break
              }
            }
          });
      }
    }, []);

    const onConfirmUser = async (code: string) => {
      try {
        const message = await dispatch(confirmShopify(code)).unwrap();
        await dispatch(fetchShopifyUser()).unwrap().catch(err => console.log(err));
        dispatch(getUser()).unwrap()

        await Swal.fire({
          title: translate('notifications.titles.success'),
          text: decoratePopUpMessage(message as string),
          confirmButtonText: translate('notifications.choices.close'),
          imageUrl: iconNotifySuccess,
        });
        // window.location.reload()
        navigate(`/platform/dashboard/shopify`);
      } catch (e) {
        const error = e as IError;

        await Swal.fire({
          imageUrl: errorBigIcon,
          title: translate('notifications.titles.error'),
          text: decoratePopUpMessage(error.error as string),
          confirmButtonText: translate('notifications.choices.close'),
        });
      }
    };

    const sendCodeDialog = useCallback(async () => {
      if (codeSent) {
        return confirmUser();
      }

      return Swal.fire({
        title: translate('forms.shopify_add_edit_user.confirm_ownership'),
        text: translate('forms.shopify_add_edit_user.confirm_user'),
        showCancelButton: true,
        cancelButtonText: translate('notifications.choices.cancel'),
        denyButtonText: translate('notifications.choices.enter_code'),
        confirmButtonText: translate('notifications.choices.send'),
        imageUrl: iconNotifyWarningInfo,
        showDenyButton: true,
        showLoaderOnConfirm: true,
        preConfirm: () => {
          return sendConfirmationCode();
        },
        preDeny: () => {
          setCodeSent(true);
          confirmUser()
        },
      });
    }, [codeSent]);

    const confirmUser = async () => {
      await Swal.fire({
        text: 'Enter Confirmation Code',
        input: 'text',
        imageUrl: iconNotifyWarningInfo,
        inputAttributes: {
          autocapitalize: 'off',
        },
        confirmButtonText: translate('notifications.choices.confirm'),
        cancelButtonText: translate('notifications.choices.close'),
        denyButtonText: translate('notifications.choices.resend'),
        showCancelButton: true,
        showDenyButton: true,
        showLoaderOnConfirm: true,
        showLoaderOnDeny: true,
        customClass: {
          container: 'confirm-input-modal'
        },
        preConfirm: (code) => {
          return onConfirmUser(code);
        },
        preDeny: () => {
          return sendConfirmationCode();
        },
        allowOutsideClick: () => !Swal.isLoading(),
      });
    };

    useEffect(() => {
      if (!user) {
        return;
      }
      reset({username: user.username?.replaceAll(' ', '') as string});
      const confirmGithub = searchParams.get('githubConfirmStatus');

      if(user?.isConfirmed && user?.isAppInstalled && confirmGithub !== null && confirmGithub === 'false') {
        navigate(`/platform/dashboard/shopify`)
      }
    }, [user]);

    useEffect(() => {
      if (installBtn.current) {
        installBtn.current.setAttribute('target', '_blank')
      }
    }, [installBtn])
    const extractDomain = (input) => {
      try {
        let formatted = input.trim();
        if (!/^https?:\/\//i.test(formatted)) {
          formatted = "https://" + formatted;
        }

        const url = new URL(formatted);
        return url.hostname.toLowerCase(); // just the domain
      } catch {
        return '';
      }
    }

    const onSubmit: SubmitHandler<IGithubUser> = async (data) => {
      const adaptedData = extractDomain(data?.username?.replaceAll(' ', '') || '')

      try {
        setIsUpdatingUser(true);
        if (user && user.username) {

          const confirmationChoice = await Swal.fire({
            title: translate('notifications.titles.warning'),
            text: translate('notifications.shopify_user.change'),
            confirmButtonText: translate('notifications.choices.continue'),
            showConfirmButton: true,
            allowEscapeKey: false,
            allowOutsideClick: false,
            cancelButtonText: translate('notifications.choices.cancel'),
            showCancelButton: true,
            imageUrl: iconNotifyWarningInfo,
          });

          if (!confirmationChoice.isConfirmed) {
            return;
          }
        }

        const request = await dispatch(getShopifyLoginUrl(adaptedData)).unwrap();
        const requestUser = await dispatch(fetchShopifyUser()).unwrap();
        dispatch(getUser()).unwrap()

        const userChoice = await Swal.fire({
          title: translate('notifications.titles.success'),
          text: !!request.result?.redirectUrl ? translate('forms.shopify_add_edit_user.failed_changed') : (request.message || ''),
          imageUrl: !!request.result?.redirectUrl ? iconNotifyWarningInfo : iconNotifySuccess,
          cancelButtonText: translate('notifications.choices.nevermind'),
          showCancelButton: !!request.result?.redirectUrl,
          confirmButtonText: (!user?.username || adaptedData !== user.username) && request.result?.redirectUrl
            ? translate('forms.shopify_add_edit_user.install_github_app')
            : translate('notifications.choices.close'),
        });

        if (userChoice.isConfirmed && request.result?.redirectUrl && !requestUser?.[0]?.app_installed) {
          window.open(request.result?.redirectUrl, '_self');
        } else if (requestUser?.[0]?.shop_confirmed && userChoice.isConfirmed && !request.result?.redirectUrl){
          navigate(`/platform/dashboard/shopify`)
        } else if (!requestUser?.[0]?.shop_confirmed) {
          sendCodeDialog()
        }

        if (user && adaptedData === user.username) {
          return;
        }

      } catch (err) {
        const error = err as IError;
        console.log(err);
        if (onClose && !!subPlan) {
          onClose()
        }

        if (error.code === 404) {
          const userFailed = await Swal.fire({
            title: translate('notifications.titles.warning'),
            text: translate('forms.shopify_add_edit_user.failed_changed'),
            imageUrl: iconNotifyWarningInfo,
            cancelButtonText: translate('notifications.choices.cancel'),
            showCancelButton: true,
            confirmButtonText: translate('forms.shopify_add_edit_user.install_github_app')
          });
          if (userFailed.isConfirmed && user?.redirectUrl) {
            window.open(user?.redirectUrl, '_self');
          }
        } else if (error.code !== 404) {
          Swal.fire({
            title: translate('notifications.titles.error'),
            text: decoratePopUpMessage(error.error as string),
            imageUrl: errorBigIcon,
          });
        }
      } finally {
        setIsUpdatingUser(false);
      }
    };

  const deleteShopify = async () => {
    try {
      setIsDeleteInProgress(true)
      const userChoice = await Swal.fire({
        title: translate('notifications.titles.warning'),
        text: 'All backups will be deleted when you disconnect the shop. Are you sure you want to disconnect your shop?',
        imageUrl: errorBigIcon,
        confirmButtonText: translate('notifications.choices.confirm'),
        cancelButtonText: translate('notifications.choices.cancel'),
        showCancelButton: true,
      });

      if (userChoice.isConfirmed) {
        await dispatch(deleteShop())
        await dispatch(fetchShopifyUser()).unwrap()
        await dispatch(getUser())
        Swal.fire({
          title: translate('notifications.titles.success'),
          text: 'Shop has been successfully disconnected',
          toast: true,
          position: 'top-end',
          timerProgressBar: true,
          showConfirmButton: false,
          showCloseButton: true,
          imageUrl: toastSuccess,
          timer: 3000,
        });
      }
      setIsDeleteInProgress(false)
    } catch (err) {
      const error = err as IError;
      setIsDeleteInProgress(false)
      Swal.fire({
        title: translate('notifications.titles.error'),
        text: decoratePopUpMessage(error.error as string),
        imageUrl: errorBigIcon,
        confirmButtonText: translate('notifications.choices.close'),
      });
    }
  }

    return (
      <>
        {subPlan?.type === ESubscriptionPlans.NO_SUB && trialState === ETrialStatus.NOT_STARTED ?
          <NoSubscriptionBlock platformName={EPlatformsNames.SHOPIFY}/>
          :
          <Card mb={6}>
            <CardContent>
              <p className='newapp-step-two-auth-title'>
                Authorize BackupLABS
              </p>
              <div className='newapp-step-two-log'>
                <p className='newapp-step-two-title'>
                  Connecting to {Platform.shopify.title}
                </p>
                <div className='newapp-step-two-app-row'>
                  <div className='newapp-step-two-app'>
                    <div className='platformLogoContainer'>
                      <img className="platformLogo" src={Platform.shopify.smallLogo} loading="lazy"/>
                    </div>
                    <p className='newapp-step-two-app-title'>
                      {Platform.shopify.title}
                    </p>
                  </div>
                  {onClose && (
                    <EditSelectionButton onClick={onClose} />
                  )}
                </div>
              </div>
              <div id="github-repo-user-description">
                <form noValidate onSubmit={handleSubmit(onSubmit)}>
                  <p className='newapp-step-two-desc'>
                    {translate('forms.common.platform_auth_desc_first')}
                    <br/>
                    {translate('forms.common.platform_auth_desc_second', { platform: Platform.shopify.title })}
                  </p>
                  <div className="form-row github-user-change-block">
                    <FormControl className='input-name'>
                      <TextField
                        required
                        {...register('username', {
                          required: translate('forms.common.required') as string,
                          minLength: {
                            value: 2,
                            message: translate('forms.common.min_length'),
                          },
                        })}
                        id="user-name"
                        placeholder={translate('forms.shopify_add_edit_user.account_name')}
                        // label={translate('forms.shopify_add_edit_user.account_name')}
                        error={!!errors.username}
                        InputLabelProps={{shrink: true}}
                      />
                      {errors.username && !isUpdatingUser &&
                        <ValidationErrorBlock errorMessage={errors.username.message as string}/>
                      }
                    </FormControl>
                  </div>

                  {user && !user?.isAppInstalled && user?.redirectUrl &&
                    <div className="form-row">
                      <p className="github-app-tooltip">
                        {translate('forms.shopify_add_edit_user.github_app_required')}
                        <b>
                          <a className="link shopify-link" href={user?.redirectUrl || ''} target="_blank"
                             rel="noreferrer">
                            {translate('forms.shopify_add_edit_user.github_app')}
                          </a>
                        </b>
                      </p>
                    </div>
                  }
                  <div className="action-buttons">
                    {onClose && !subPlan && (
                      <BackButton onClick={onClose} />
                    )}
                    {user?.username && !user?.isAppInstalled && (
                      <Button
                        ref={installBtn}
                        mr={4}
                        variant="contained"
                        color="primary"
                        href={user?.redirectUrl || ''}
                      >
                        {translate('forms.shopify_add_edit_user.github_app')}
                        <img src={expIcon} className="feather-icon-in-button icon-ml"/>
                      </Button>
                    )}
                    {user?.username && !user?.isConfirmed && user?.isAppInstalled && (
                      <Button
                        mr={4}
                        variant="contained"
                        color="primary"
                        onClick={sendCodeDialog}
                      >
                        {translate('notifications.choices.confirm_user')}
                        {/*<ExternalLink className="feather-icon-in-button icon-ml"/>*/}
                      </Button>
                    )}
                    <Button
                      onClick={handleSubmit(onSubmit)}
                      variant="contained"
                      color="primary"
                      disabled={!isValid || isUpdatingUser || !isDirty}
                    >
                      {isUpdatingUser &&
                        <div className="small-spinner-wrapper">
                          <CircularProgress color="inherit" style={{width: '20px', height: '20px', marginTop: '6px'}}/>
                        </div>}
                      {isUpdatingUser
                        ? translate('notifications.choices.processing')
                        : !user?.username
                          ? translate('forms.shopify_add_edit_user.add_new_account')
                          : translate('forms.shopify_add_edit_user.change_account')
                      }
                      <img src={expIcon} className="feather-icon-in-button icon-ml"/>
                    </Button>
                    {user?.username && (
                      <Button
                        onClick={deleteShopify}
                        variant="outlined"
                        color="error"
                        ml={4}
                        disabled={isDeleteInProgress}
                      >
                        {isDeleteInProgress &&
                          <div className="small-spinner-wrapper">
                            <CircularProgress color="inherit" style={{width: '20px', height: '20px', marginTop: '6px'}}/>
                          </div>
                        }
                        Disconnect shop
                      </Button>
                    )}
                  </div>
                </form>
              </div>
            </CardContent>
          </Card>
        }
      </>
    );
  }
;

export default ShopifyUserAddEdit;
